import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { GlobalJobordersService } from "src/app/_services/global-joborders.service";
import { ErrorUtil } from "src/app/_utilities/error";
import { AuthenticationService } from "src/app/auth/authentication.service";
import moment from "moment-timezone";
import { ModalServiceService } from "src/app/_services/modal-service.service";
import { NgxSpinnerService } from "ngx-spinner";
import { DeleteRecordService } from "src/app/_services/delete-record.service";
import { UtilServiceService } from "src/app/_services/utilService/util-service.service";

interface SuggestedUserCheck {
  label: string;
  state: 'ok' | 'warn';
}

interface TimelineBlock {
  startHour: number;
  endHour: number;
  kind: 'busy' | 'new' | 'overlap';
  label: string;
}

interface SuggestedUser {
  rank: number;
  name: string;
  initials: string;
  isRulePick: boolean;
  distanceLabel: string;
  jobsTodayLabel: string;
  liveStatusLabel: string;
  isLive: boolean;
  checks: SuggestedUserCheck[];
  timeline: TimelineBlock[];
  showDetails: boolean;
}

interface FallbackGroup {
  order: string;
  tag: string;
  name: string;
  isActive: boolean;
}

@Component({
  selector: "app-preview-suggested-users",
  standalone: false,
  templateUrl: "./preview-suggested-users.component.html",
  styleUrl: "./preview-suggested-users.component.scss",
})
export class PreviewSuggestedUsersComponent
  extends SubscriptionUtil
  implements OnInit, OnDestroy
{
  translate: any;
  ruleName: string = "";
  countdownLabel: string = "";
  isScheduledStartInFuture: boolean = false;
  skillsRequired = [];
  private readonly skillsPreviewLimit = 5;
  showAllSkills = false;
  visibleSkills: any;
  hiddenSkillsCount: any;
  fallbackGroups: FallbackGroup[] = [];
  axisStart = 0;
  axisEnd = 24;
  axisLabels = [
    { time: "00:00", hour: 0 },
    { time: "04:00", hour: 4 },
    { time: "08:00", hour: 8 },
    { time: "12:00", hour: 12 },
    { time: "16:00", hour: 16 },
    { time: "20:00", hour: 20 },
  ];

  suggestedUsers: SuggestedUser[] = [];
  data: any;
  suggestedAssignUsersInfo: any; //related to smart assign feature
  dateFormat: any;
  timezone: any;
  dataLoaded: any;
  isAppliedToAllTasks: boolean = false;
  startTime: any;
  endTime: any;

  constructor(
    public modalRef: BsModalRef,
    private bsModalServc: BsModalService,
    private errorUtil: ErrorUtil,
    private languageTranslateService: LanguageTranslateService,
    private globalJobOrdersService: GlobalJobordersService,
    private auth: AuthenticationService,
    private modalService: ModalServiceService,
    private spinner: NgxSpinnerService,
    private deleteServc: DeleteRecordService,
    private router: Router,
    private util: UtilServiceService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getLanguageData();
    this.dateFormat = localStorage.getItem("date_format");
    this.timezone = this.auth.getUserTimezone();
  }
  ngOnDestroy(): void {
    super.ngOnDestroy();
  }
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
          this.getSmartAssignInfo(this.data.joId, {});
        }
      }),
    );
  }

  isSameDay(start?: any, end?: any, tz?: any): boolean {
    if (!start || !end) return false;
    return moment(start).tz(tz).isSame(moment(end).tz(tz), "day");
  }

  getSmartAssignInfo(jobOrderId: number, body: any) {
    // Call the service method to get smart assign info
    this.spinner.show();
    this.push(
      this.globalJobOrdersService
        .getJOSmartAssignInfo(jobOrderId, body)
        .subscribe({
          next: (resp: any) => {
            this.spinner.hide();
            this.dataLoaded = true;
            if (resp.status == 200) {
              this.suggestedAssignUsersInfo = resp.body;
              this.startTime = this.suggestedAssignUsersInfo.scheduledStart
                ? moment.tz(
                    this.suggestedAssignUsersInfo.scheduledStart,
                    this.timezone,
                  )
                : null;

              this.endTime = this.suggestedAssignUsersInfo.scheduledEnd
                ? moment.tz(
                    this.suggestedAssignUsersInfo.scheduledEnd,
                    this.timezone
                  )
                : null;

              this.ruleName = this.suggestedAssignUsersInfo.ruleName;
              this.updateCountdownLabel();
              let skillList: any =
                this.suggestedAssignUsersInfo.mandatorySkills;
              this.skillsRequired = skillList;

              this.visibleSkills = this.skillsRequired.slice(
                0,
                this.skillsPreviewLimit,
              );
              this.hiddenSkillsCount = Math.max(
                0,
                this.skillsRequired.length - this.skillsPreviewLimit,
              );

              this.fallbackGroups =
                this.suggestedAssignUsersInfo.userGroups.map(
                  (ug: any, i: number) => {
                    return {
                      order: ug.level,
                      tag: ug.levelLabel,
                      name: ug.userGroupName,
                      isActive: ug.selected,
                    };
                  },
                );

              this.suggestedUsers =
                this.suggestedAssignUsersInfo.suggestedUsers;
              this.suggestedUsers.map((s: any) => {
                s["formattedDistance"] = s.distanceInMeters
                  ? parseFloat(
                      (parseFloat(s.distanceInMeters) / 1000).toFixed(2),
                    ) + " km"
                  : "-";
                s["checks"] = [
                  { label: s.skillsMatchLabel, state: "ok" },
                  {
                    label: s.availabilityLabel,
                    state: s.availabilityWarning ? "warn" : "ok",
                  },
                  {
                    label: s.shiftLabel,
                    state: s.shiftWarning ? "warn" : "ok",
                  },
                  { label: s.leaveLabel, state: "ok" },
                  { label: s.serviceZoneLabel, state: "ok" },
                ];
                s["showDetails"] = false;
                s.scheduleSlots.map((slot: any) => {
                  const localStart = moment.tz(slot.start, this.timezone);
                  const localEnd = moment.tz(slot.end, this.timezone);

                  slot["startHour"] = parseFloat(
                    (localStart.hours() + localStart.minutes() / 60).toFixed(1),
                  );
                  slot["endHour"] = parseFloat(
                    (localEnd.hours() + localEnd.minutes() / 60).toFixed(1),
                  );

                  slot["kind"] = slot.thisJob ? "new" : "busy";
                  let hoursLabel: string = `${this.formatHourToTime(slot.startHour)}-${this.formatHourToTime(slot.endHour)}`;
                  slot["label"] = slot.thisJob ? "This job" : slot.jobOrderNumber;

                  return { ...slot };
                });
                //the following code is to test overlap scenario
                // s.scheduleSlots.push(
                //   {
                //     startHour: 8,
                //     endHour: 9.5,
                //     kind: "busy",
                //     label: "08:00–09:30",
                //   },
                //   { startHour: 19, endHour: 23, label: "19:00–23:00" },
                // );
                this.markOverlappingSlots(s.scheduleSlots);
                return { ...s };
              });
            }
          },
          error: (error: any) => {
            this.spinner.hide();
            this.dataLoaded = true;
            console.error("Error fetching smart assign info:", error);
            this.errorUtil.setErrorMessage(
              error.status,
              error.error ? error.error.title : null,
              error.error ? error.error.title : null,
              "error",
              2000,
            );
          },
        }),
    );
  }

  push(obs: any) {
    super.push(obs);
  }

  toggleSkills() {
    this.showAllSkills = !this.showAllSkills;
    this.visibleSkills = this.showAllSkills
      ? this.skillsRequired
      : this.skillsRequired.slice(0, this.skillsPreviewLimit);
  }

  private updateCountdownLabel(): void {
    const scheduledStart = this.suggestedAssignUsersInfo?.scheduledStart;
    if (!scheduledStart) {
      this.isScheduledStartInFuture = false;
      this.countdownLabel = "";
      return;
    }

    const start = moment.tz(scheduledStart, this.timezone);
    const now = moment.tz(this.timezone);
    this.isScheduledStartInFuture = start.isAfter(now);

    if (!this.isScheduledStartInFuture) {
      this.countdownLabel = "";
      return;
    }

    const minutesRemaining = start.diff(now, "minutes");
    const hours = Math.floor(minutesRemaining / 60);
    const minutes = minutesRemaining % 60;
    this.countdownLabel =
      hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
  }

  private formatHourToTime(hour: number): string {
    const hours = Math.floor(hour);
    const minutes = Math.round((hour - hours) * 60);
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  private markOverlappingSlots(slots: any[]) {
    const thisJobSlot = slots.find((slot: any) => slot.thisJob);
    if (!thisJobSlot) {
      return;
    }
    const isOverlapping = slots.some(
      (slot: any) =>
        !slot.thisJob &&
        slot.startHour < thisJobSlot.endHour &&
        thisJobSlot.startHour < slot.endHour,
    );
    if (isOverlapping) {
      thisJobSlot.kind = "overlap";
    }
  }

  slotTooltip(slot: any): string {
    const start = moment.tz(slot.start, this.timezone);
    const end = moment.tz(slot.end, this.timezone);
    const dateFormat = this.dateFormat?.toUpperCase() || "DD/MM/YYYY";
    const orderDate = start.isSame(end, "day")
      ? `${start.format("h:mm A")} - ${end.format("h:mm A")}, ${start.format(dateFormat)}`
      : `${start.format("h:mm A")} , ${start.format(dateFormat)} - ${end.format("h:mm A")} , ${end.format(dateFormat)}`;

    const rows = [
      { label: "JO Number", value: slot.jobOrderNumber },
      { label: "Order Name", value: slot.jobOrderName },
      { label: "Order Date", value: orderDate },
    ]
      .map((row) => `<div class="mp-su-slot-tip__row"><span class="mp-su-slot-tip__label">${row.label}:</span> ${this.escapeHtml(row.value ?? "-")}</div>`)
      .join("");
    return `<div class="mp-su-slot-tip__title">Job Details</div>${rows}`;
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  blockStyle(block: TimelineBlock) {
    const span = this.axisEnd - this.axisStart;
    const left = ((block.startHour - this.axisStart) / span) * 100;
    const width = ((block.endHour - block.startHour) / span) * 100;
    return { left: left + "%", width: width + "%" };
  }

  axisLabelStyle(hour: number, index: number) {
    const span = this.axisEnd - this.axisStart;
    const left = ((hour - this.axisStart) / span) * 100;
    let transform = "translateX(-50%)";
    if (index === 0) {
      transform = "translateX(0)";
    } else if (index === this.axisLabels.length - 1) {
      transform = "translateX(-100%)";
    }
    return { left: left + "%", transform };
  }

  toggleDetails(user: SuggestedUser) {
    user.showDetails = !user.showDetails;
  }

  //tags the suggested user who already owns this job order
  isCurrentAssignee(user: any): boolean {
    const ownerId = this.data?.joInfo?.orderOwner?.id;
    return !!ownerId && user?.userId == ownerId;
  }

  onAssign(user: any) {
    let data: any = {
      deleteType: "Confirm Smart JO Assign",
      deleteHeader: "Assignment Confirmation",
      deleteIconType: "confirm",
      message:
        "Are you sure that you want to assign " +
        this.data.joInfo.jobOrderNumber +
        " to " +
        user.fullName +
        "?",
      id: this.data.joId,
      userId: user.userId,
      applyToAllTask: this.isAppliedToAllTasks,
    };

    this.push(
      this.deleteServc.getItems(data).subscribe(() => {
        this.bsModalServc.setDismissReason("smart-assign-done");
        this.modalRef.hide();
      }),
    );
  }

  onAssignSomeoneElse() {
    this.bsModalServc.setDismissReason("assignSomeoneElse");
    this.modalRef.hide();
    if (this.data.from == "scheduler") {
      //if the modal is open from scheduler page
      this.modalService.openModal("ownerAssign", this.data.joInfo);
    }
    else{
      this.modalService.openModal("assignJoOwner", this.data.joInfo);
    }
  }

  onSelectUserFallback() {
    this.bsModalServc.setDismissReason("selectUserFallback");
    this.modalRef.hide();
    if (this.data.from == "scheduler") {
      //if the modal is open from scheduler page
      this.modalService.openModal("ownerAssign", this.data.joInfo);
    }
    else{
      this.modalService.openModal("assignJoOwner", this.data.joInfo);
    }
  }

  onCancelClick() {
    this.bsModalServc.setDismissReason("cancel");
    this.modalRef.hide();
  }

  viewAssignmentRule() {
    // TODO: backend does not return a rule id on suggestedAssignUsersInfo yet -
    // suggest-users-detailed needs to include typeConfigId alongside ruleName.
    let url = this.router.serializeUrl(
      this.router.createUrlTree(
        ["dashboard/setup/jobOrder/assignment-rule/view"],
        {
          queryParams: {
            typeConfigId: this.suggestedAssignUsersInfo.ruleId,
          },
        },
      ),
    );
    url = url.replace("%23", "#");
    this.util.openItemToNewTab(url);
  }

  darkenHexColor(hex: string, amount: number = 0.4): string {
    if (!hex || typeof hex !== "string") {
      return "#3B8B3B";
    }

    let cleaned = hex.trim().replace("#", "");
    if (cleaned.length === 3) {
      cleaned = cleaned
        .split("")
        .map((c) => c + c)
        .join("");
    }
    if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) {
      return "#3B8B3B";
    }

    const num = parseInt(cleaned, 16);
    let r = (num >> 16) & 255;
    let g = (num >> 8) & 255;
    let b = num & 255;

    r = Math.max(0, Math.floor(r * (1 - amount)));
    g = Math.max(0, Math.floor(g * (1 - amount)));
    b = Math.max(0, Math.floor(b * (1 - amount)));

    return (
      "#" +
      ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()
    );
  }
}
